import { Button } from '@kizen/kds/Button';
import { Typography } from '@kizen/kds/Typography';
import MultiSelect from '__components/Inputs/MultiSelect';
import Select from '__components/Inputs/Select';
import Input from '__components/Kizen/Input';
import BasicModal from '__components/Modals/presets/BasicModal';
import { useState } from 'react';
import { ModalConfig, ModalCancelEventSource } from './PluginContext';

interface PluginModalProps {
  modalState: {
    props: any;
    config: ModalConfig;
  };
}

const getInitialValues = (config: ModalConfig) => {
  const initialValues: Record<string, any> = {};

  config.content?.forEach((item) => {
    if (item.type === 'text' && item.defaultValue) {
      initialValues[item.id] = item.defaultValue;
    } else if (item?.type === 'dropdown' && item.defaultValue) {
      if (item.multiselect) {
        const options = item.options?.filter((opt) =>
          item.defaultValue?.includes(opt.value)
        );
        if (options?.length > 0) {
          initialValues[item.id] = options;
        }
      } else {
        const option = item.options?.find(
          (opt) => opt.value === item.defaultValue
        );
        if (option) {
          initialValues[item.id] = option;
        }
      }
    }
  });

  return initialValues;
};

export const PluginModal = ({ modalState }: PluginModalProps) => {
  const [values, setValues] = useState<Record<string, any>>(() =>
    getInitialValues(modalState.config)
  );

  if (!modalState) {
    return null;
  }

  const { props, config = {} } = modalState;

  return (
    <BasicModal
      rightBtn={
        <Button
          variant={config.confirmButton?.variant}
          color={config.confirmButton?.color ?? 'primary'}
        >
          {config.confirmButton?.label ?? 'Confirm'}
        </Button>
      }
      actionButton={false}
      leftBtn={
        <Button
          variant={config.cancelButton?.variant ?? 'text'}
          color={config.cancelButton?.color ?? 'secondary'}
        />
      }
      defaultLeftBtnHandler={(...args: any) =>
        props?.onHide?.(ModalCancelEventSource.button, ...args)
      }
      defaultLeftBtnText={config.cancelButton?.label ?? 'Cancel'}
      {...props}
      heading={config.title}
      onConfirm={() => {
        props?.onConfirm?.(values);
      }}
      onHide={(...args: any) =>
        props?.onHide?.(ModalCancelEventSource.close, ...args)
      }
    >
      <div className="grid gap-[10px] grid-cols-2">
        {(config.content ?? []).map((item: any, index: number) => {
          let key = item.id;

          if (item.type === 'spacer') {
            key = `spacer-${index}`;
          } else if (item.type === 'description') {
            key = `description-${index}`;
          }

          if (!key) {
            return null;
          }

          return (
            <div
              key={item.id}
              className={item.widthPercent === 50 ? 'col-span-1' : 'col-span-2'}
            >
              {(
                {
                  text: (
                    <div>
                      <Input
                        label={item.label}
                        placeholder={item.placeholder}
                        variant="outline"
                        onChange={(val: string) => {
                          setValues({ ...values, [item.id]: val });
                        }}
                        value={values[item.id] ?? ''}
                      />
                    </div>
                  ),
                  description: (
                    <div>
                      <Typography>{item.content}</Typography>
                    </div>
                  ),
                  spacer: (
                    <div
                      className="h-[var(--spacer-height)]"
                      style={
                        {
                          '--spacer-height': `${item.height}px`,
                        } as any
                      }
                    />
                  ),
                  dropdown: item.multiselect ? (
                    <MultiSelect
                      options={item.options}
                      onChange={(v: any) => {
                        setValues({ ...values, [item.id]: v });
                      }}
                      value={values[item.id]}
                      label={item.label}
                      placeholder={item.placeholder}
                      menuInline
                    />
                  ) : (
                    <Select
                      options={item.options}
                      onChange={(v: any) =>
                        setValues({ ...values, [item.id]: v })
                      }
                      value={values[item.id]}
                      label={item.label}
                      placeholder={item.placeholder}
                      menuInline
                    />
                  ),
                } as any
              )[item.type] ?? null}
            </div>
          );
        })}
      </div>
    </BasicModal>
  );
};
